<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="640px">
    <v-card v-if="Student" v-draggable-dialog class="teacher-form-dialog-card">
      <div class="dialog-drag-handle teacher-form-dialog-header">
        <div class="teacher-form-dialog-heading">
          <span class="teacher-form-dialog-icon">
            <v-icon color="#2f7d4a">mdi-account-edit-outline</v-icon>
          </span>
          <div>
            <p>Edit student</p>
            <h2>{{ studentDisplayName }}</h2>
            <small>Update the student information visible to teachers.</small>
          </div>
        </div>
        <div class="teacher-form-dialog-header__actions">
          <v-icon small color="#a0aaa3" aria-hidden="true">mdi-drag</v-icon>
          <v-btn icon aria-label="Close edit student" @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-divider />

      <v-card-text class="teacher-form-dialog-body">
        <section class="teacher-form-dialog-section">
          <div class="teacher-form-dialog-section__heading">
            <div>
              <h3>Student details</h3>
              <p>Keep profile and contact information current.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.trim="Student.firstName"
                outlined
                dense
                autofocus
                :error-messages="nameErrors"
                label="First name"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.trim="Student.lastName" outlined dense label="Last name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="Student.gender"
                outlined
                dense
                :items="genderOptions"
                item-text="text"
                item-value="value"
                label="Gender"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="Student.dob"
                outlined
                dense
                type="date"
                :max="maxDob"
                label="Date of birth"
                persistent-hint
                hint="Optional"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.trim="Student.phoneNumber" outlined dense label="Phone number" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.trim="Student.email" outlined dense label="Email address" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model.trim="Student.reference" outlined dense label="Reference" />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="Student.description"
                outlined
                dense
                auto-grow
                rows="2"
                :label="$t('labels.description')"
              />
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-divider />
      <v-card-actions class="teacher-form-dialog-actions">
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Save changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import studentService from "@/modules/Students/service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

function emptyStudent() {
  return {
    firstName: null,
    lastName: null,
    email: null,
    gender: "male",
    dob: null,
    phoneNumber: null,
    reference: null,
    description: null,
  };
}

export default {
  name: "update-Student",
  mixins: [validationMixin],
  props: {
    editDialog: {
      default: false,
    },
    editStudent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      Student: emptyStudent(),
      dialog: false,
      loading: false,
      genderOptions: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" },
      ],
    };
  },
  validations: {
    Student: {
      firstName: { required },
    },
  },
  computed: {
    maxDob() {
      return this.$moment().format("YYYY-MM-DD");
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required) errors.push("First name is required");
      return errors;
    },
    studentDisplayName() {
      const name = [this.Student.firstName, this.Student.lastName].filter(Boolean).join(" ").trim();
      return name || "Student details";
    },
  },
  watch: {
    editDialog(val) {
      this.dialog = val;
      if (!val || !this.editStudent) return;

      this.Student = {
        ...this.editStudent,
        dob: this.editStudent.dob ? this.$moment(this.editStudent.dob).format("YYYY-MM-DD") : null,
      };
    },
  },
  methods: {
    close() {
      this.dialog = false;
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Student = emptyStudent();
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        }).fire({
          icon: "error",
          title: this.$t("toastMessage.text.invalidFields"),
        });
        return;
      }

      this.loading = true;
      const payload = {
        ...this.Student,
        dob: this.Student.dob || null,
      };

      return studentService.update(this.Student.id, payload)
        .then((result) => {
          if (result.status) {
            this.$swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            }).fire({ icon: "success", title: "Student is updated" });
            this.$emit("submit", { student: payload });
            this.close();
          }
        })
        .catch((err) => {
          this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          }).fire({
            icon: "error",
            title: err && err.data ? err.data.message : "Student could not be updated",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
